import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { canActivateLayout } from './layout/layout.guard';

export const favoritesRoutePath = 'favorites';
export const profileRoutePath = 'profile';
export const searchRoutePath = 'search';
export const loginRoutePath = 'login';
export const loginEmailRoutePath = 'email';
export const forgotPasswordRoutePath = 'forgot';
export const resetPasswordRoutePath = 'reset';
export const signupRoutePath = 'sign-up';
export const verseRoutePath = 'verse';
export const accountRoutePath = 'account';
export const publicRoutePath = 'public';
export const toolsRoutePath = 'tools';
export const settingsRoutePath = 'settings';
export const notesRoutePath = 'notes';
export const chatsRoutePath = 'chats';
export const imagesRoutePath = 'images';
export const libraryRoutePath = 'library';
export const userRoutePath = 'user';
export const feedRoutePath = 'feed';
export const findRoutePath = 'find';
export const postRoutePath = 'post';
export const publicPostRoutePath = 'p';
export const copyrightRoutePath = 'copyright';
type RoutePath =
  | typeof favoritesRoutePath
  | typeof profileRoutePath
  | typeof searchRoutePath
  | typeof loginRoutePath
  | typeof signupRoutePath
  | typeof loginEmailRoutePath
  | typeof forgotPasswordRoutePath
  | typeof resetPasswordRoutePath
  | typeof verseRoutePath
  | typeof accountRoutePath
  | typeof publicRoutePath
  | typeof toolsRoutePath
  | typeof settingsRoutePath
  | typeof notesRoutePath
  | typeof chatsRoutePath
  | typeof imagesRoutePath
  | typeof libraryRoutePath
  | typeof userRoutePath
  | typeof feedRoutePath
  | typeof findRoutePath
  | typeof postRoutePath
  | typeof publicPostRoutePath
  | typeof copyrightRoutePath;

export type LibrarySegment =
  | 'all'
  | typeof notesRoutePath
  | typeof chatsRoutePath
  | typeof imagesRoutePath;

export function getRoutePath(routePath: RoutePath) {
  return `/${routePath}`;
}

export function getFavoritesRoutePath(path?: typeof verseRoutePath) {
  if (!path) {
    return getRoutePath(favoritesRoutePath);
  }

  return `${getRoutePath(favoritesRoutePath)}/${path}`;
}

export function getSearchRoutePath(path?: typeof verseRoutePath) {
  if (!path) {
    return getRoutePath(searchRoutePath);
  }

  return `${getRoutePath(searchRoutePath)}/${path}`;
}

export function getLoginRoutePath(
  path?: typeof loginEmailRoutePath | typeof forgotPasswordRoutePath
) {
  if (!path) {
    return getRoutePath(loginRoutePath);
  }

  return `${getRoutePath(loginRoutePath)}/${path}`;
}

export function getLibraryRoutePath() {
  return getRoutePath(libraryRoutePath);
}

// Relative commands for linking to a public profile from inside a tab
// section, mirroring how verse links use ['verse', id].
export function getUserRouterLink(username: string) {
  return [userRoutePath, username];
}

// Legacy tab URLs (/notes, /chats, /images) land on the Library page with the
// matching segment preselected.
function redirectToLibrary(segment: LibrarySegment) {
  return () =>
    inject(Router).createUrlTree([getLibraryRoutePath()], {
      queryParams: { segment },
    });
}

// The children every tab section repeats so verse details, chats, and the
// side-menu pages render inside whichever tab is active.
export function tabSectionChildren(): Routes {
  return [
    {
      path: `${userRoutePath}/:username`,
      title: 'Profile | Hey Bible',
      loadComponent: () =>
        import('./social/user/user.page').then((m) => m.UserPage),
    },
    {
      path: `${verseRoutePath}/:id`,
      title: 'Verse | Hey Bible',
      loadComponent: () =>
        import('./verse/verse.page').then((m) => m.VersePage),
    },
    {
      path: `${verseRoutePath}/:id/chat`,
      title: 'Chat | Hey Bible',
      loadComponent: () =>
        import('./verse/chat-page/chat.page').then((m) => m.ChatPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/${accountRoutePath}`,
      title: 'Account | Hey Bible',
      loadComponent: () =>
        import('./account/account.page').then((m) => m.AccountPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/${profileRoutePath}`,
      title: 'Profile | Hey Bible',
      loadComponent: () =>
        import('./profile/profile.page').then((m) => m.ProfilePage),
    },
    {
      path: `${verseRoutePath}/:id/chat/${toolsRoutePath}`,
      title: 'Tools | Hey Bible',
      canActivate: [
        () => import('./tools/admin.guard').then((m) => m.canActivateAdmin),
      ],
      loadComponent: () =>
        import('./tools/tools.page').then((m) => m.ToolsPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/${settingsRoutePath}`,
      title: 'Settings | Hey Bible',
      loadComponent: () =>
        import('./settings/settings.page').then((m) => m.SettingsPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/:conversationId`,
      title: 'Chat | Hey Bible',
      loadComponent: () =>
        import('./verse/chat-page/chat.page').then((m) => m.ChatPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/:conversationId/${accountRoutePath}`,
      title: 'Account | Hey Bible',
      loadComponent: () =>
        import('./account/account.page').then((m) => m.AccountPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/:conversationId/${profileRoutePath}`,
      title: 'Profile | Hey Bible',
      loadComponent: () =>
        import('./profile/profile.page').then((m) => m.ProfilePage),
    },
    {
      path: `${verseRoutePath}/:id/chat/:conversationId/${toolsRoutePath}`,
      title: 'Tools | Hey Bible',
      canActivate: [
        () => import('./tools/admin.guard').then((m) => m.canActivateAdmin),
      ],
      loadComponent: () =>
        import('./tools/tools.page').then((m) => m.ToolsPage),
    },
    {
      path: `${verseRoutePath}/:id/chat/:conversationId/${settingsRoutePath}`,
      title: 'Settings | Hey Bible',
      loadComponent: () =>
        import('./settings/settings.page').then((m) => m.SettingsPage),
    },
    {
      path: accountRoutePath,
      title: 'Account | Hey Bible',
      loadComponent: () =>
        import('./account/account.page').then((m) => m.AccountPage),
    },
    {
      path: profileRoutePath,
      title: 'Profile | Hey Bible',
      loadComponent: () =>
        import('./profile/profile.page').then((m) => m.ProfilePage),
    },
    {
      path: toolsRoutePath,
      title: 'Tools | Hey Bible',
      canActivate: [
        () => import('./tools/admin.guard').then((m) => m.canActivateAdmin),
      ],
      loadComponent: () =>
        import('./tools/tools.page').then((m) => m.ToolsPage),
    },
    {
      path: settingsRoutePath,
      title: 'Settings | Hey Bible',
      loadComponent: () =>
        import('./settings/settings.page').then((m) => m.SettingsPage),
    },
  ];
}

export const routes: Routes = [
  {
    path: '',
    redirectTo: searchRoutePath,
    pathMatch: 'full',
  },
  {
    path: '',
    canActivate: [canActivateLayout],
    loadComponent: () =>
      import('./layout/layout.component').then((m) => m.LayoutComponent),
    children: [
      {
        path: favoritesRoutePath,
        children: [
          {
            path: '',
            title: 'Favorites | Hey Bible',
            loadComponent: () =>
              import('./favorites/favorites.page').then((m) => m.FavoritesPage),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: searchRoutePath,
        children: [
          {
            path: '',
            title: 'Search | Hey Bible',
            loadComponent: () =>
              import('./search/search.page').then((m) => m.SearchPage),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: libraryRoutePath,
        children: [
          {
            path: '',
            title: 'Library | Hey Bible',
            loadComponent: () =>
              import('./library/library.page').then((m) => m.LibraryPage),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: feedRoutePath,
        children: [
          {
            path: '',
            title: 'Feed | Hey Bible',
            loadComponent: () =>
              import('./feed/feed.page').then((m) => m.FeedPage),
          },
          {
            path: findRoutePath,
            title: 'Find People | Hey Bible',
            loadComponent: () =>
              import('./social/find/find-users.page').then(
                (m) => m.FindUsersPage
              ),
          },
          {
            path: `${postRoutePath}/:id`,
            title: 'Post | Hey Bible',
            loadComponent: () =>
              import('./social/post/post.page').then((m) => m.PostPage),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: profileRoutePath,
        title: 'Profile | Hey Bible',
        loadComponent: () =>
          import('./profile/profile.page').then((m) => m.ProfilePage),
      },
      {
        path: accountRoutePath,
        title: 'Account | Hey Bible',
        loadComponent: () =>
          import('./account/account.page').then((m) => m.AccountPage),
      },
      {
        path: toolsRoutePath,
        title: 'Tools | Hey Bible',
        canActivate: [
          () => import('./tools/admin.guard').then((m) => m.canActivateAdmin),
        ],
        loadComponent: () =>
          import('./tools/tools.page').then((m) => m.ToolsPage),
      },
      {
        path: settingsRoutePath,
        title: 'Settings | Hey Bible',
        loadComponent: () =>
          import('./settings/settings.page').then((m) => m.SettingsPage),
      },
      {
        path: notesRoutePath,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: redirectToLibrary(notesRoutePath),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: chatsRoutePath,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: redirectToLibrary(chatsRoutePath),
          },
          ...tabSectionChildren(),
        ],
      },
      {
        path: imagesRoutePath,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: redirectToLibrary(imagesRoutePath),
          },
          ...tabSectionChildren(),
        ],
      },
    ],
  },
  {
    title: 'Sign Up | Hey Bible',
    path: signupRoutePath,
    loadComponent: () =>
      import('./sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    title: 'Reset Password | Hey Bible',
    path: resetPasswordRoutePath,
    canActivate: [
      () =>
        import('./reset-password/reset-password.guard').then(
          (m) => m.canActivateResetPassword
        ),
    ],
    loadComponent: () =>
      import('./reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPage
      ),
  },
  {
    title: 'Login | Hey Bible',
    path: loginRoutePath,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./login/login.page').then((m) => m.LoginPage),
      },
      {
        path: loginEmailRoutePath,
        loadComponent: () =>
          import('./login/login-with-email/login-with-email.page').then(
            (m) => m.LoginWithEmailPage
          ),
      },
      {
        path: forgotPasswordRoutePath,
        title: 'Forgot Password | Hey Bible',
        loadComponent: () =>
          import('./login/forgot-password/forgot-password.page').then(
            (m) => m.ForgotPasswordPage
          ),
      },
    ],
  },
  {
    path: 'public',
    loadComponent: () =>
      import('./public/public.page').then((m) => m.PublicPage),
  },
  {
    // Unauthenticated share page for posts (heybible.app/p/:id).
    path: `${publicPostRoutePath}/:id`,
    title: 'Shared Post | Hey Bible',
    loadComponent: () =>
      import('./social/public-post/public-post.page').then(
        (m) => m.PublicPostPage
      ),
  },
  {
    path: copyrightRoutePath,
    title: 'Copyright | Hey Bible',
    loadComponent: () =>
      import('./copyright/copyright.page').then((m) => m.CopyrightPage),
  },
  {
    // Return page after Stripe checkout
    path: 'checkout/redirect',
    loadComponent: () =>
      import('./checkout/redirect.page').then((m) => m.CheckoutRedirectPage),
  },
  {
    path: '**',
    title: '404 | Hey Quotey',
    loadComponent: () => import('./404/404.page').then((m) => m.NotFoundPage),
  },
];
